import React, { Fragment } from 'react';
import type { PortableTextBlock } from 'sanity';
import { ProductCard, PromotionCard } from '@/components/shared/cards';
import { MenuItemLink, PaddingTopBottomType } from '@/types/types';
import { sectionPaddingTopBottom } from '../utils/utils';

export type CardType = {
  product: {
    variantGid: string;
    slug: string;
    categorySlug: string;
    price?: number;
    compareAtPrice?: number;
    sanityTitle: string;
  };
  title: PortableTextBlock;
  description?: PortableTextBlock;
  image: {
    url: string;
    alt?: string;
    blurDataURL: string;
  };
  features?: [
    {
      text: string;
      image: {
        url: string;
        alt?: string;
      };
    },
  ];
  compatibleWith?: [
    {
      text: string;
      product: {
        title: string;
        slug: string;
        categorySlug: string;
      };
    },
  ];
  showPaymentOptions?: boolean;
  useProductTitle?: boolean;
  productPage?: {
    slug: string;
  };
};

export type PromotionCardType = {
  title: PortableTextBlock;
  image: {
    url: string;
    alt?: string;
    blurDataURL: string;
  };
  buttons?: [
    {
      text?: string;
      link: MenuItemLink;
    },
  ];
};

export default function ProductCardGrid(props: {
  data?: {
    sectionId: string;
    cards: CardType[];
    promotionCards?: PromotionCardType[];
    showPatterns: boolean;
    displayOptions: {
      padding: PaddingTopBottomType;
    };
  };
}) {
  const sectionId = props.data?.sectionId;
  const cards = props.data?.cards;
  const promotionCards = props.data?.promotionCards;
  const showPatterns = props.data?.showPatterns;
  const displayOptions = props.data?.displayOptions;

  return (
    <div className='overflow-x-clip'>
      <section
        id={sectionId}
        className={`${sectionPaddingTopBottom(displayOptions?.padding)} relative scroll-m-16 bg-white`}
      >
        <div className='absolute bottom-0 left-0 right-0 top-0 h-[100%] bg-gradient-to-b from-white via-yellow-50 to-white'></div>

        <div className='container-padding container relative mx-auto'>
          {showPatterns && (
            <>
              {/* Small Pattern */}
              <svg
                width='44'
                height='43'
                viewBox='0 0 44 43'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
                className='absolute -top-32 right-32 z-0 hidden h-10 w-10 xl:block'
              >
                <path
                  d='M28.2876 27.6377L15.478 27.6127L15.5014 15.5969L28.311 15.6219L28.2876 27.6377ZM1.2571 29.4568L14.5694 29.4828L14.5453 41.8407C14.5447 42.1426 14.7856 42.3846 15.0872 42.3852L28.7615 42.4119C29.0631 42.4124 29.305 42.1714 29.3056 41.8695L29.3316 28.5253L42.5031 28.551C42.8047 28.5515 43.0465 28.3105 43.0471 28.0086L43.0737 14.3222C43.0743 14.0203 42.8335 13.7783 42.5319 13.7778L29.2196 13.7518L29.2436 1.39384C29.2442 1.09193 29.0034 0.849939 28.7017 0.849352L15.0275 0.822705C14.7258 0.822117 14.4841 1.06317 14.4835 1.36508L14.4575 14.7093L1.28588 14.6836C0.984246 14.683 0.742486 14.9241 0.741898 15.226L0.715267 28.8922C0.714679 29.1941 0.955497 29.4361 1.25714 29.4367'
                  fill='#D64C19'
                />
              </svg>

              {/* Medium Pattern */}
              <svg
                width='70'
                height='70'
                viewBox='0 0 70 70'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
                className='absolute -right-20 -top-36 z-20 hidden h-20 w-20 xl:block'
              >
                <path
                  d='M27.8513 28.1507C28.153 28.1513 28.3947 27.9103 28.3953 27.6084L28.4202 14.8277L41.632 14.8535L41.6071 27.6341C41.6065 27.936 41.8473 28.178 42.149 28.1786L54.4559 28.2026L54.4302 41.3656L41.6608 41.3407C41.3592 41.3402 41.1174 41.5812 41.1168 41.8831L41.0913 54.9858L28.6638 54.9616L28.6892 41.9193C28.6898 41.6174 28.449 41.3754 28.1473 41.3748L15.0361 41.3492L15.0619 28.1258L27.8312 28.1507L27.8513 28.1507ZM0.857535 42.1066L13.9687 42.1321L13.9425 55.5769C13.9419 55.8788 14.1828 56.1208 14.4845 56.1214L27.8169 56.1474L27.7915 69.1897C27.7909 69.4916 28.0317 69.7336 28.3334 69.7342L42.0076 69.7608C42.3092 69.7614 42.5511 69.5204 42.5517 69.2185L42.5772 56.0957L55.3466 56.1205C55.6482 56.1211 55.89 55.8801 55.8906 55.5782L55.9168 42.1333L68.6862 42.1582C68.9878 42.1588 69.2296 41.9178 69.2302 41.6159L69.2568 27.9496C69.2574 27.6477 69.0166 27.4057 68.7149 27.4051L56.4081 27.3812L56.4342 13.9967C56.4347 13.6948 56.1939 13.4528 55.8922 13.4522L42.4593 13.4261L42.4842 0.625289C42.4848 0.323384 42.244 0.0813902 41.9423 0.0808024L28.2882 0.0541948C27.9865 0.053607 27.7448 0.294661 27.7442 0.596566L27.7193 13.3571L14.2863 13.3309C13.9847 13.3303 13.7429 13.5714 13.7423 13.8733L13.7161 27.3181L0.966856 27.2933C0.665217 27.2927 0.423397 27.5337 0.422808 27.8356L0.396177 41.5019C0.395589 41.8038 0.636468 42.0457 0.938107 42.0463'
                  fill='#D64C19'
                />
              </svg>

              {/* Big Pattern */}
              <svg
                width='148'
                height='151'
                viewBox='0 0 148 151'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
                className='absolute -right-10 -top-20 z-0 hidden h-40 w-40 xl:block'
              >
                <path
                  d='M26.9298 81.3669L14.9447 81.3435L14.9669 69.9517L26.952 69.975L26.9298 81.3669ZM40.0047 69.0947L28.4619 69.0722L28.4854 57.0564L40.0281 57.0789L40.0047 69.0947ZM53.4039 55.4948L41.3182 55.4713L41.342 43.2743L53.4276 43.2979L53.4039 55.4948ZM66.8622 42.4988L54.5152 42.4748L54.5409 29.2513L66.888 29.2754L66.8622 42.4988ZM79.8601 28.4554L67.7744 28.4318L67.7995 15.5707L79.8852 15.5942L79.8601 28.4554ZM93.5477 42.2691L81.4621 42.2455L81.4873 29.3039L93.5729 29.3274L93.5477 42.2691ZM107.056 55.3176L94.9702 55.294L94.9939 43.0971L107.08 43.1206L107.056 55.3176ZM120.342 68.6877L108.257 68.6642L108.279 56.91L120.365 56.9336L120.342 68.6877ZM108.59 83.2972L120.676 83.3207L120.653 94.7126L108.568 94.6891L108.59 83.2972ZM95.2523 96.2733L107.338 96.2968L107.314 108.494L95.2286 108.47L95.2523 96.2733ZM67.952 123.049L80.0377 123.073L80.0126 135.934L67.9269 135.911L67.952 123.049ZM54.7476 108.955L67.0946 108.979L67.0688 122.202L54.7218 122.178L54.7476 108.955ZM41.6014 95.9071L53.687 95.9307L53.6633 108.128L41.5776 108.104L41.6014 95.9071ZM53.0117 81.297L41.5495 81.2746L41.5713 70.084L53.0335 70.1064L53.0117 81.297ZM66.6902 68.8448L54.6045 68.8213L54.6274 57.0671L66.7131 57.0907L66.6902 68.8448ZM93.6567 68.8974L81.3096 68.8733L81.333 56.8575L93.6801 56.8816L93.6567 68.8974ZM81.6214 94.6366L81.6431 83.5064L93.769 83.53C93.769 83.53 93.9098 83.4699 93.9903 83.4298L93.9684 94.6606L81.6214 94.6366ZM79.6967 81.349L67.611 81.3254L67.6328 70.1348L79.7185 70.1584L79.6967 81.349ZM54.938 83.4543L67.0236 83.4779L67.0025 94.3464L54.9169 94.3229L54.938 83.4543ZM68.2864 95.9591L80.6334 95.9832L80.6091 108.442L68.2621 108.418L68.2864 95.9591ZM106.663 81.6632L94.5368 81.6395C94.5368 81.6395 94.3959 81.6996 94.3154 81.7397L94.3384 69.9252L106.686 69.9493L106.663 81.6833L106.663 81.6632ZM80.3503 55.5272L68.0032 55.5031L68.0275 43.0445L80.3745 43.0686L80.3503 55.5272ZM28.7754 83.6851L40.318 83.7076L40.2974 94.3145L28.7547 94.292L28.7754 83.6851ZM93.7538 122.254L81.6682 122.231L81.6934 109.289L93.7791 109.313L93.7538 122.254ZM133.348 81.7353L121.262 81.7117L121.285 69.9777L133.37 70.0013L133.348 81.7353ZM0.851689 69.1795C0.55005 69.1789 0.30823 69.4199 0.307641 69.7218L0.281246 83.2673C0.280657 83.5692 0.521537 83.8112 0.823175 83.8118L14.1355 83.8377L14.1129 95.4107C14.1123 95.7126 14.3532 95.9546 14.6548 95.9552L26.9416 95.9792L26.9175 108.357C26.9169 108.659 27.1577 108.901 27.4593 108.902L40.0879 108.926L40.0621 122.17C40.0615 122.472 40.3023 122.714 40.604 122.714L53.293 122.739L53.2666 136.244C53.2661 136.546 53.5069 136.788 53.8085 136.789L66.8594 136.814L66.8337 149.977C66.8331 150.279 67.074 150.521 67.3757 150.522L80.9091 150.548C81.2108 150.549 81.4526 150.308 81.4532 150.006L81.4788 136.843L94.0471 136.867C94.3487 136.868 94.5905 136.627 94.5911 136.325L94.6169 123.081L107.788 123.107C108.09 123.108 108.332 122.867 108.332 122.565L108.358 109.321L121.349 109.346C121.65 109.347 121.892 109.106 121.893 108.804L121.917 96.3253L134.687 96.3501C134.988 96.3507 135.23 96.1097 135.23 95.8078L135.255 83.3492L147.381 83.3728C147.682 83.3734 147.924 83.1323 147.925 82.8304L147.951 69.285C147.952 68.9831 147.711 68.7411 147.409 68.7405L134.921 68.7161L134.946 55.9154C134.947 55.6135 134.706 55.3715 134.404 55.3709L121.635 55.346L121.659 42.8673C121.66 42.5654 121.419 42.3234 121.117 42.3228L108.127 42.2975L108.153 29.0539C108.153 28.752 107.912 28.51 107.611 28.5094L94.4393 28.4838L94.4651 15.2603C94.4657 14.9584 94.2248 14.7164 93.9231 14.7158L81.3549 14.6914L81.3805 1.5283C81.3811 1.22639 81.1403 0.984403 80.8386 0.983816L67.3051 0.957443C67.0035 0.956855 66.7617 1.19791 66.7611 1.49981L66.7355 14.6629L53.6846 14.6374C53.383 14.6368 53.1411 14.8779 53.1405 15.1798L53.1142 28.685L40.4253 28.6603C40.1237 28.6597 39.8819 28.9007 39.8813 29.2027L39.8555 42.4462L27.2269 42.4216C26.9253 42.421 26.6835 42.6621 26.6829 42.964L26.6587 55.3421L14.372 55.3181C14.0704 55.3175 13.8286 55.5586 13.828 55.8605L13.802 69.2047L0.851689 69.1795Z'
                  fill='#D64C19'
                />
              </svg>
            </>
          )}
          <div className='relative z-10 grid items-stretch gap-8 md:grid-cols-2 md:gap-6 lg:gap-8 xl:grid-cols-3 xl:gap-10 2xl:gap-12'>
            {cards &&
              cards.length > 0 &&
              cards.map((card, index) => (
                <Fragment key={index}>
                  <ProductCard card={card} />
                </Fragment>
              ))}
            {promotionCards &&
              promotionCards.length > 0 &&
              promotionCards.map((card, index) => (
                <Fragment key={index}>
                  <PromotionCard card={card} />
                </Fragment>
              ))}
          </div>
        </div>
      </section>{' '}
    </div>
  );
}
